import { useContext } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { ManagedAccountContext } from "../../context/ManagedAccountContext";
import { useManagedAccountInfo } from "./useManagedAccountInfo";

const WSU_RED = "#A60F2D";

const chipSx = {
    backgroundColor: "#fdf0f2",
    border: `1px solid ${WSU_RED}`,
    color: WSU_RED,
    fontWeight: 500,
    fontSize: "0.75rem",
    height: "auto",
    maxWidth: "100%",
    "& .MuiChip-label": {
        display: "block",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        py: 0.5,
    },
};

const getSections = (isSharedMailbox) => [
    {
        heading: "Identity",
        fields: [
            { label: "Name", key: "name" },
            { label: "Display Name", key: "displayName" },
            { label: "User Principal Name", key: "userPrincipalName" },
            { label: "DN", key: "dn" },
        ],
    },
    {
        heading: "Contact & Organization",
        fields: [
            { label: "Telephone Number", key: "telephoneNumber" },
            { label: "Department", key: "department" },
            { label: "Manager", key: "manager" },
        ],
    },
    {
        heading: "Mail & Delegation",
        fields: [
            { label: isSharedMailbox ? "Full Access Granted" : "Delegates", key: "delegates" },
            { label: "Proxy Addresses", key: "proxyAddresses" },
        ],
    },
];

const valueLength = (value) => {
    if (Array.isArray(value)) return value.join(", ").length;
    if (value === null || value === undefined) return 0;
    return String(value).length;
};

const renderCell = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {value.map((v) => (
                    <Chip
                        key={v}
                        label={v}
                        size="small"
                        sx={chipSx}
                    />
                ))}
            </Box>
        ) : (
            <Typography variant="body2" color="text.disabled" fontStyle="italic">None</Typography>
        );
    }

    return value ? (
        <Typography variant="body2">{value}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
};

// Returns one "tile" per section, each carrying the length of its longest
// value so the grid can decide how much width the tile needs.
export const useManagedAccountSections = () => {
    const { isLoaded, error } = useManagedAccountInfo();
    const { managedAccountModule } = useContext(ManagedAccountContext);

    if (!isLoaded || error) {
        return { isLoaded, error, tiles: [] };
    }

    const data = managedAccountModule?.data || {};
    const isSharedMailbox = managedAccountModule.dn?.includes("OU=Shared");
    const sections = getSections(isSharedMailbox);

    const tiles = sections.map((section) => {
        const rows = section.fields.map(({ label, key }) => {
            const value = data[key];
            return { key, label, content: renderCell(value), length: valueLength(value) };
        });
        return {
            key: section.heading,
            heading: section.heading,
            rows,
            maxLength: rows.reduce((max, row) => Math.max(max, row.length), 0),
        };
    });

    return { isLoaded, error, tiles };
};
